import { Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { UserCredentialRequest } from '../models/HttpBase/request-api';
import { UserApp } from '../models/aplicacion/user-app';
import {
  PwaAuthMeResponse,
  PwaAuthTokenResponse,
  PwaAuthUserProfileResponse
} from '../models/pwa/pwa-auth.dto';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private oUser: UserApp = new UserApp();
  private readonly sessionStorageKey = 'bgw-pwa-auth';
  private readonly legacySessionStorageKey = 'bgw-app';
  private readonly guestStorageKey = 'bgw-pwa-guest-id';
  private readonly deviceKeyStorageKey = 'bgw-pwa-device-key';
  private readonly internalRoles = new Set([
    'MASTER_ADMIN',
    'ADMIN',
    'MODERADOR',
    'MODERADOR_OBSERVACIONES',
    'OPERADOR',
    'JUEZ',
    'PWA_ADMIN',
  ]);

  public readonly user = signal<UserApp | null>(null);

  constructor(private readonly router: Router) {
    this.loadSavedSession();
    if (this.oUser && this.oUser.Id > 0) {
      this.user.set(this.oUser);
    }
  }

  public getUserApp(): UserApp {
    return this.user() ?? this.oUser;
  }

  public getAccessToken(): string {
    return this.getUserApp().Token ?? '';
  }

  public getRefreshToken(): string {
    return this.getUserApp().RefreshToken ?? '';
  }

  public isProfileComplete(): boolean {
    return !!this.getUserApp().PerfilCompleto;
  }

  public getUserCredentials(): UserCredentialRequest | null {
    const currentUser = this.getUserApp();
    if (currentUser && currentUser.Id > 0) {
      return {
        Id: currentUser.Id,
        Name: currentUser.Name || undefined
      };
    }

    return null;
  }

  public setSessionFromAuthResponse(response: PwaAuthTokenResponse): void {
    const user = new UserApp();
    this.applyProfileFields(user, response);
    user.Token = response.Token ?? '';
    user.RefreshToken = response.RefreshToken ?? '';
    user.TokenExpiration = response.TokenExpirationUtc ?? null;
    user.Roles = this.resolveRoles(response);

    this.setUserApp(user);
  }

  public setSessionFromMeResponse(response: PwaAuthMeResponse): void {
    const current = this.getUserApp();
    const user = new UserApp();
    this.applyProfileFields(user, response);
    user.Token = current.Token ?? '';
    user.RefreshToken = current.RefreshToken ?? '';
    user.TokenExpiration = current.TokenExpiration ?? null;
    user.CompanyId = current.CompanyId ?? 0;
    user.Roles = this.resolveRoles(response, current.Roles);

    this.setUserApp(user);
  }

  public setUserApp(user: UserApp): void {
    try {
      this.oUser = this.hydrateUser(user);
      this.user.set(this.oUser);
      this.saveSessionUser();
    } catch (error) {
      console.log(error);
    }
  }

  public setActivateToken(
    newToken: string,
    refreshToken?: string,
    expiration?: string | null
  ): void {
    try {
      this.oUser.Token = newToken;
      if (refreshToken !== undefined) {
        this.oUser.RefreshToken = refreshToken;
      }
      if (expiration !== undefined) {
        this.oUser.TokenExpiration = expiration;
      }

      const current = this.user();
      if (current) {
        this.user.set({
          ...current,
          Token: newToken,
          RefreshToken: refreshToken ?? current.RefreshToken,
          TokenExpiration: expiration ?? current.TokenExpiration
        });
      }
      this.saveSessionUser();
    } catch (error) {
      console.log(error);
    }
  }

  public isAuthenticated(): boolean {
    const user = this.getUserApp();
    return !!user.Id && !!user.Token;
  }

  public getUserRoles(): string[] {
    return this.normalizeRoles(this.getUserApp().Roles);
  }

  public hasRole(role: string): boolean {
    const normalizedRole = this.normalizeRole(role);
    if (!normalizedRole) {
      return false;
    }

    return this.getUserRoles().includes(normalizedRole);
  }

  public hasAnyRole(roles: readonly string[]): boolean {
    if (!roles.length) {
      return false;
    }

    const userRoles = new Set(this.getUserRoles());
    return roles.some((role) => userRoles.has(this.normalizeRole(role)));
  }

  public hasInternalRole(): boolean {
    return this.hasAnyRole(Array.from(this.internalRoles));
  }

  public destroySession(): void {
    try {
      sessionStorage.removeItem(this.sessionStorageKey);
      sessionStorage.removeItem(this.legacySessionStorageKey);
    } catch (error) {
      console.log(error);
    }
  }

  public logout(): void {
    this.oUser = new UserApp();
    this.user.set(null);
    this.destroySession();
    this.router.navigate(['/']);
  }

  public setGuestId(guestId: string): void {
    try {
      sessionStorage.setItem(this.guestStorageKey, guestId);
    } catch (error) {
      console.log(error);
    }
  }

  public getGuestId(): string | null {
    try {
      return sessionStorage.getItem(this.guestStorageKey);
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  public clearGuestId(): void {
    try {
      sessionStorage.removeItem(this.guestStorageKey);
    } catch (error) {
      console.log(error);
    }
  }

  public getOrCreateDeviceKey(): string {
    try {
      const existing = localStorage.getItem(this.deviceKeyStorageKey);
      if (existing) {
        return existing;
      }

      const generated = this.generateDeviceKey();
      localStorage.setItem(this.deviceKeyStorageKey, generated);
      return generated;
    } catch (error) {
      console.log(error);
      return this.generateDeviceKey();
    }
  }

  private saveSessionUser(): void {
    try {
      sessionStorage.setItem(this.sessionStorageKey, JSON.stringify(this.oUser));
    } catch (error) {
      console.log(error);
    }
  }

  private loadSavedSession(): void {
    const serialized =
      sessionStorage.getItem(this.sessionStorageKey) ??
      sessionStorage.getItem(this.legacySessionStorageKey);
    if (!serialized) {
      return;
    }

    try {
      this.oUser = this.hydrateUser(JSON.parse(serialized) as Partial<UserApp>);
    } catch (error) {
      try {
        this.oUser = this.hydrateUser(JSON.parse(atob(serialized)) as Partial<UserApp>);
      } catch (decodeError) {
        console.log(decodeError);
      }
    }

    if (this.isObsoleteMockSession(this.oUser)) {
      this.oUser = new UserApp();
      this.destroySession();
    }
  }

  private generateDeviceKey(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }

    return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  private isObsoleteMockSession(user: UserApp | null): boolean {
    if (!user) {
      return false;
    }

    return user.Token === 'mock-token' || user.RefreshToken === 'mock-refresh';
  }

  private hydrateUser(source: Partial<UserApp> | null | undefined): UserApp {
    const hydrated = new UserApp();
    if (!source || typeof source !== 'object') {
      return hydrated;
    }

    Object.assign(hydrated, source);
    hydrated.Roles = this.normalizeRoles(source.Roles);
    return hydrated;
  }

  private applyProfileFields(target: UserApp, response: PwaAuthUserProfileResponse): void {
    target.Id = response.UsuarioID;
    target.Name = this.normalizeText(response.NombreCompleto);
    target.Email = this.normalizeText(response.Email);
    target.PerfilCompleto = !!response.PerfilCompleto;
    target.EsCabana = !!response.EsCabana;
    target.EsProductor = !!response.EsProductor;
    target.Ubicacion = this.normalizeText(response.Ubicacion);
    target.InteresAsociarse = !!response.InteresAsociarse;
    target.Telefono = this.normalizeText(response.Telefono);
    target.ContactoNombreEmpresa = this.normalizeText(response.ContactoNombreEmpresa);
    target.AvatarCode = this.normalizeAvatarCode(response.AvatarCode);
    target.AvatarUrl = this.normalizeText(response.AvatarUrl);
  }

  private resolveRoles(
    response: PwaAuthUserProfileResponse,
    fallbackRoles: unknown = []
  ): string[] {
    const hasRoleArrays = Array.isArray(response.Roles) || Array.isArray(response.roles);
    const hasRoleFlags =
      response.EsAdmin !== undefined ||
      response.IsAdmin !== undefined ||
      response.EsModerador !== undefined ||
      response.EsOperador !== undefined ||
      response.EsJuez !== undefined;

    if (!hasRoleArrays && !hasRoleFlags) {
      return this.normalizeRoles(fallbackRoles);
    }

    const resolved: unknown[] = [];
    if (Array.isArray(response.Roles)) {
      resolved.push(...response.Roles);
    }
    if (Array.isArray(response.roles)) {
      resolved.push(...response.roles);
    }

    if (response.EsAdmin || response.IsAdmin) {
      resolved.push('ADMIN');
    }
    if (response.EsModerador) {
      resolved.push('MODERADOR');
    }
    if (response.EsOperador) {
      resolved.push('OPERADOR');
    }
    if (response.EsJuez) {
      resolved.push('JUEZ');
    }

    return this.normalizeRoles(resolved);
  }

  private normalizeRoles(value: unknown): string[] {
    if (!Array.isArray(value)) {
      return [];
    }

    const unique = new Set<string>();
    value.forEach((role) => {
      const normalized = this.normalizeRole(role);
      if (!normalized) {
        return;
      }
      unique.add(normalized);
    });
    return Array.from(unique);
  }

  private normalizeRole(value: unknown): string {
    if (typeof value !== 'string') {
      return '';
    }

    return value
      .trim()
      .toUpperCase()
      .replace(/[\s-]+/g, '_');
  }

  private normalizeAvatarCode(value: unknown): string {
    if (typeof value !== 'string') {
      return '';
    }

    return value.trim().toUpperCase();
  }

  private normalizeText(value: unknown): string {
    if (typeof value !== 'string') {
      return '';
    }

    return value.trim();
  }
}
